import { useState, useMemo } from 'react';
import { useStore } from '../../../hooks/useStore';
import { useAuth } from '../../../hooks/useAuth';
import { getSupplyItemsByHtx, addSupplyItem, updateSupplyItem, deleteSupplyItem } from '../../../store';
import { SUPPLY_FILTER_CHIPS } from '../../../utils/status';
import { showToast } from '../../../components/base/Toast';
import { EmptyState } from '../../../components/base/EmptyState';
import { confirmModal } from '../../../components/base/ConfirmModal';
import { SupplyStats } from './components/SupplyStats';
import { SupplyFilters } from './components/SupplyFilters';
import { SupplyCard } from './components/SupplyCard';
import { SupplyFormModal } from './components/SupplyFormModal';
import { SupplyDetailModal } from './components/SupplyDetailModal';
import { InStockModal } from './components/InStockModal';
import { OutStockModal } from './components/OutStockModal';
import { AdjustModal } from './components/AdjustModal';
import type { SupplyItem } from '../../../types';

export default function HtxSupplyPage() {
  useStore();
  const { user } = useAuth();
  const items = getSupplyItemsByHtx(user?.htxId);
  const [filters, setFilters] = useState({ search: '', nhom: 'Tất cả', status: '' });
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<SupplyItem | null>(null);
  const [detailItem, setDetailItem] = useState<SupplyItem | null>(null);
  const [inStockItem, setInStockItem] = useState<SupplyItem | null>(null);
  const [outStockItem, setOutStockItem] = useState<SupplyItem | null>(null);
  const [adjustItem, setAdjustItem] = useState<SupplyItem | null>(null);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!item.maVatTu.toLowerCase().includes(q) && !item.name.toLowerCase().includes(q) && !(item.nhaCungCap?.toLowerCase().includes(q))) return false;
      }
      if (filters.nhom !== 'Tất cả' && item.nhom !== filters.nhom) return false;
      if (filters.status) {
        const chip = SUPPLY_FILTER_CHIPS.find((c) => c.key === filters.status);
        if (chip) {
          if (chip.stockCheck === 'zero' && item.stock > 0) return false;
          if (chip.stockCheck === 'low') {
            const threshold = item.ngưỡngCảnhBáo || 0;
            if (item.stock <= 0) return false;
            if (threshold > 0 && item.stock > threshold) return false;
            if (threshold === 0 && item.status !== 'Sắp hết') return false;
          }
          if (chip.statusMatch && !(chip.statusMatch as readonly string[]).includes(item.status)) return false;
        }
      }
      return true;
    });
  }, [items, filters]);

  const handleSave = async (data: Omit<SupplyItem, 'id'>) => {
    if (!user?.htxId) return;
    try {
      if (editItem) {
        // stock/available/reserved are left out on purpose: quantities only move
        // through Nhập/Xuất/Điều chỉnh so each change keeps a reason in the
        // ledger. Cleared optional fields are sent as ''/0 rather than omitted,
        // otherwise "xóa giá nhập" or "bỏ nhà cung cấp" would never save.
        await updateSupplyItem(editItem.id, {
          maVatTu: data.maVatTu,
          name: data.name,
          nhom: data.nhom,
          unit: data.unit,
          giaNhap: data.giaNhap ?? 0,
          giaCungUng: data.giaCungUng ?? 0,
          nhaCungCap: data.nhaCungCap ?? '',
          supplierId: data.supplierId ?? '',
          loHang: data.loHang ?? '',
          hanSuDung: data.hanSuDung ?? '',
          ngưỡngCảnhBáo: data.ngưỡngCảnhBáo ?? 0,
          ghiChu: data.ghiChu ?? '',
        }, user.htxId);
        showToast('Cập nhật vật tư thành công', 'success');
      } else {
        await addSupplyItem(data, user.htxId);
        showToast('Thêm vật tư thành công', 'success');
      }
      setFormOpen(false);
      setEditItem(null);
    } catch (e) {
      showToast(e instanceof Error ? e.message : editItem ? 'Cập nhật vật tư thất bại' : 'Thêm vật tư thất bại', 'error');
    }
  };

  const handleDelete = async (item: SupplyItem) => {
    if (!user?.htxId) return;
    const ok = await confirmModal({
      title: 'Xóa vật tư',
      message: `Xóa "${item.name}" (${item.maVatTu}) khỏi kho vật tư? Thao tác này không thể hoàn tác.

Vật tư đã cấp cho nông hộ không thể xóa — hãy chuyển sang "Ngừng cung ứng" thay vì xóa.`,
      confirmText: 'Xóa',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteSupplyItem(item.id, user.htxId);
      // Any sheet still pointing at the row that just disappeared has to go too.
      setDetailItem((d) => (d?.id === item.id ? null : d));
      setInStockItem((i) => (i?.id === item.id ? null : i));
      setOutStockItem((o) => (o?.id === item.id ? null : o));
      setAdjustItem((a) => (a?.id === item.id ? null : a));
      showToast('Đã xóa vật tư', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Xóa vật tư thất bại', 'error');
    }
  };

  return (
    <div className="px-4 py-4 pb-4 space-y-4 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Quản lý cung ứng</h2>
        </div>
        <button
          type="button"
          onClick={() => { setEditItem(null); setFormOpen(true); }}
          className="bg-agrida-700 hover:bg-agrida-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap min-h-[44px] flex items-center gap-2"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
          Thêm vật tư
        </button>
      </div>

      <SupplyStats />
      <SupplyFilters filters={filters} onChange={setFilters} resultCount={filtered.length} />

      {filtered.length === 0 ? (
        <EmptyState icon="ri-box-3-line" title="Không có kết quả" description="Thử thay đổi bộ lọc hoặc thêm vật tư mới." />
      ) : (
        /* overflow-x-auto, not overflow-hidden: below ~1000px the fixed columns
           no longer fit and the name column — the only thing identifying the
           row — used to be the one that collapsed. Now the table scrolls. */
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto mb-6">
          {/* Desktop header */}
          <div className="hidden lg:grid min-w-[1000px] items-center px-4 py-2.5 bg-slate-50 border-b border-slate-200 gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide" style={{ gridTemplateColumns: '80px minmax(0, 1fr) 96px 68px 76px 68px 88px 92px 188px' }}>
            <div>Mã</div>
            <div>Tên vật tư</div>
            <div className="text-center">Nhóm</div>
            <div className="text-right">Tồn</div>
            <div className="text-right">Khả dụng</div>
            <div className="text-right">Đã cấp</div>
            <div className="text-right">Giá nhập</div>
            <div className="text-center">Trạng thái</div>
            <div className="text-right">Thao tác</div>
          </div>

          <div>
            {filtered.map((item) => (
              <SupplyCard
                key={item.id}
                item={item}
                onView={setDetailItem}
                onEdit={(it) => { setEditItem(it); setFormOpen(true); }}
                onInStock={setInStockItem}
                onOutStock={setOutStockItem}
                onAdjust={setAdjustItem}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      <SupplyFormModal open={formOpen} item={editItem} htxId={user?.htxId} onClose={() => { setFormOpen(false); setEditItem(null); }} onSave={handleSave} />
      <SupplyDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      <InStockModal item={inStockItem} onClose={() => setInStockItem(null)} />
      <OutStockModal item={outStockItem} onClose={() => setOutStockItem(null)} />
      <AdjustModal item={adjustItem} onClose={() => setAdjustItem(null)} />
    </div>
  );
}